import React, { createContext, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import LocalStorage, { SESSION_KEY } from "../../utilities/localStorage";

const FeatureContext = createContext({ features: new Set(), has: () => false });

export function useFeature(name) {
  const ctx = useContext(FeatureContext);
  return ctx.has(name);
}

/**
 * Non-hook utility for reading feature flags outside React components
 * (e.g. Redux API files, helper.js). Reads directly from LocalStorage.
 */
export function getFeatureFlag(name) {
  const loginUser = LocalStorage.getItem(SESSION_KEY);
  if (loginUser?.features && Array.isArray(loginUser.features)) {
    return loginUser.features.includes(name);
  }
  const stored = LocalStorage.getItem("features");
  return Array.isArray(stored) ? stored.includes(name) : false;
}

export function FeatureProvider({ children }) {
  // Read features from Redux so the context reacts immediately after login
  // without needing a page reload.
  const reduxFeatures = useSelector((state) => state.login?.features);

  // Fall back to LocalStorage for page refreshes (Redux is empty on cold load
  // until the persist layer rehydrates, but LocalStorage is always available).
  const storedFeatures = LocalStorage.getItem(SESSION_KEY)?.features
    || LocalStorage.getItem("features")
    || [];

  const featureArray =
    Array.isArray(reduxFeatures) && reduxFeatures.length > 0
      ? reduxFeatures
      : storedFeatures;

  const features = useMemo(() => new Set(featureArray), [featureArray]);

  const has = (name) => features.has(name);

  return (
    <FeatureContext.Provider value={{ features, has }}>
      {children}
    </FeatureContext.Provider>
  );
}
